/* ============================================
   ProFRSH Variant Picker - Variant Selection
   ============================================ */
window.Profrsh = window.Profrsh || {};

Profrsh.VariantPicker = class {
  constructor(section) {
    this.section = section;
    if (!this.section) return;

    this.productData = null;
    this.currentVariant = null;
    this.options = [];

    const dataEl = this.section.querySelector('[data-product-json]');
    if (dataEl) {
      try { this.productData = JSON.parse(dataEl.textContent); }
      catch (e) { console.error('[VariantPicker] Invalid product JSON'); return; }
    }

    if (!this.productData) return;

    this.form = this.section.querySelector('[data-product-form]');
    this.variantIdInput = this.form?.querySelector('input[name="id"]');
    this.priceEl = this.section.querySelector('[data-price]');
    this.comparePriceEl = this.section.querySelector('[data-product-compare-price]');
    this.badgeEl = this.section.querySelector('[data-product-badge]');
    this.addToCartBtn = this.form?.querySelector('[type="submit"]');
    this.addToCartTextEl = this.addToCartBtn?.querySelector('[data-add-to-cart-text]');
    this.addToCartText = this.addToCartBtn?.dataset.addToCartText || 'Add to Cart';
    this.soldOutText = this.addToCartBtn?.dataset.soldOutText || 'Sold Out';
    this.unavailableText = this.addToCartBtn?.dataset.unavailableText || 'Unavailable';

    this.initOptions();
    this.bindEvents();

    /* Set initial variant from URL or first available */
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    if (urlVariantId) {
      this.currentVariant = this.productData.variants.find(v => v.id === parseInt(urlVariantId));
    }
    if (!this.currentVariant) {
      this.currentVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }
    this.updateDOM();
  }

  initOptions() {
    this.optionSelectors = this.section.querySelectorAll('[data-option-position]');
  }

  bindEvents() {
    /* Button-style variant selectors */
    this.section.querySelectorAll('[data-option-value]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const position = parseInt(btn.closest('[data-option-position]').dataset.optionPosition);
        const value = btn.dataset.optionValue;

        /* Update active state */
        btn.closest('[data-option-position]').querySelectorAll('[data-option-value]').forEach(b => {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        this.onOptionChange(position, value);
      });
    });

    /* Dropdown-style variant selectors */
    this.section.querySelectorAll('select[data-option-select]').forEach(select => {
      select.addEventListener('change', () => {
        const position = parseInt(select.dataset.optionSelect);
        this.onOptionChange(position, select.value);
      });
    });
  }

  onOptionChange(position, value) {
    /* Build selected options array */
    const selectedOptions = [];
    this.section.querySelectorAll('[data-option-position]').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition);
      if (pos === position) {
        selectedOptions[pos - 1] = value;
      } else {
        const activeBtn = group.querySelector('[data-option-value].is-active');
        const activeSelect = group.querySelector('select[data-option-select]');
        selectedOptions[pos - 1] = activeBtn?.dataset.optionValue || activeSelect?.value || '';
      }
    });

    /* Find matching variant */
    const variant = this.productData.variants.find(v => {
      return v.options.every((opt, i) => opt === selectedOptions[i]);
    });

    this.currentVariant = variant || null;
    this.updateDOM();
    this.updateURL();

    document.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variant: this.currentVariant, product: this.productData }
    }));
  }

  updateDOM() {
    if (!this.currentVariant) {
      /* Variant unavailable */
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        if (this.addToCartTextEl) {
          this.addToCartTextEl.textContent = this.unavailableText;
        }
      }
      return;
    }

    /* Update hidden variant ID input */
    if (this.variantIdInput) {
      this.variantIdInput.value = this.currentVariant.id;
    }

    /* Update price */
    if (this.priceEl) {
      this.priceEl.textContent = Profrsh.formatMoney(this.currentVariant.price);
    }

    /* Update compare-at price */
    if (this.comparePriceEl) {
      if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
        this.comparePriceEl.textContent = Profrsh.formatMoney(this.currentVariant.compare_at_price);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }

    /* Update sale badge */
    if (this.badgeEl) {
      if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
        const discount = Math.round((1 - this.currentVariant.price / this.currentVariant.compare_at_price) * 100);
        this.badgeEl.textContent = `-${discount}%`;
        this.badgeEl.hidden = false;
      } else {
        this.badgeEl.hidden = true;
      }
    }

    /* Update add-to-cart button */
    if (this.addToCartBtn) {
      if (this.currentVariant.available) {
        this.addToCartBtn.disabled = false;
        if (this.addToCartTextEl) {
          this.addToCartTextEl.textContent = this.addToCartText;
        }
      } else {
        this.addToCartBtn.disabled = true;
        if (this.addToCartTextEl) {
          this.addToCartTextEl.textContent = this.soldOutText;
        }
      }
    }

    /* Mark unavailable options */
    this.updateOptionAvailability();
  }

  updateOptionAvailability() {
    if (!this.currentVariant || !this.productData) return;

    this.section.querySelectorAll('[data-option-position]').forEach(group => {
      const position = parseInt(group.dataset.optionPosition);

      group.querySelectorAll('[data-option-value]').forEach(btn => {
        const optValue = btn.dataset.optionValue;

        /* Build a test options array with this value in this position */
        const testOptions = [...this.currentVariant.options];
        testOptions[position - 1] = optValue;

        /* Check if any variant matches these options */
        const matchingVariant = this.productData.variants.find(v =>
          v.options[position - 1] === optValue
        );

        const isUnavailable = !matchingVariant || !matchingVariant.available;
        btn.classList.toggle('is-unavailable', isUnavailable);
      });
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-section]').forEach(section => {
    new Profrsh.VariantPicker(section);
  });
});

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('[data-product-section]');
  if (section) new Profrsh.VariantPicker(section);
});
